import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import localStorageService from '@/utility/localStorageService';
import { t } from '@/utility/localization';

import { getDefaultVisibilityType } from '@/features/Announcements/utils';

import {
  createMessageByAdmin,
  deleteMagicReachMessage,
  fetchAllDraftsByAdmin,
  fetchAllSentByAdmin,
  getBucketDataByAdmin,
  getMessageByAdmin,
  removeScheduleFromMessage,
  updateMessageByAdmin
} from '@/services/community/communitiesMagicReachService';

import { rootNode } from '@/components/common/Editor/constants';
import {
  showErrorToast,
  showToast
} from '@/components/common/ToastContainer';

import useQueryParams from '@/hooks/useQueryParams';

import { MAGIC_REACH_MESSAGE_STATUS } from '@/pages/portal/magic-reach/components/constants';

import { useAuthContext } from '../AuthContext';
import { useCommunityPortalContext } from '../CommunityPortalContext';

export const MagicReachContext = createContext({
  buckets: [],
  bucketMap: {},
  activeDraft: null,
  allDrafts: [],
  deleteMessage: async (_id, _isDraft) => {},
  createDraft: async (_params) => {},
  updateActiveDraft: (_newId) => {}
});
export const COMMUNITY_MAGIC_REACH_KEY = 'activeDraftId';

export const MagicReachContextProvider = ({ children }) => {
  const { currentRouterQuery } = useQueryParams();
  const { activeCommunity } = useCommunityPortalContext();
  const { user } = useAuthContext();
  const [visibilityType, setVisibilityType] = useState(
    getDefaultVisibilityType(activeCommunity)
  );

  const storedActiveDraftId = localStorageService.getItem(
    COMMUNITY_MAGIC_REACH_KEY
  );

  const [allDrafts, setAllDrafts] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [allSent, setAllSent] = useState([]);

  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [loadingSent, setLoadingSent] = useState(false);
  const [draftsInitialized, setDraftsInitialized] = useState(false);

  const [errorDrafts, setErrorDrafts] = useState();
  const [errorBucket, setErrorBucket] = useState();
  const [errorSent, setErrorSent] = useState();
  const [activeDraft, setActiveDraft] = useState(null);

  const [isEditingScheduledMessage, setIsEditingScheduledMessage] =
    useState(false);
  const [hasSavedSchedule, setHasSavedSchedule] = useState(false);

  const fetchDraft = async (draftId) => {
    const { data } = await getMessageByAdmin(
      activeCommunity?._id,
      draftId
    );

    if (data?.data) {
      setActiveDraft(data?.data);
    }
  };

  useEffect(() => {
    if (storedActiveDraftId) {
      fetchDraft(storedActiveDraftId);
    }
  }, [allDrafts, allSent, storedActiveDraftId]);

  const updateMessageProgressively = (updatedMessage) => {
    setAllSent((prev) =>
      prev.map(
        (item) =>
          item._id !== updatedMessage._id
            ? item // Return the item itself if IDs do not match
            : { ...updatedMessage } // Update the item if IDs match
      )
    );
  };

  const bucketMap = useMemo(() => {
    const map = {};
    buckets.forEach((bucket) => {
      map[bucket._id] = bucket;
    });
    return map;
  }, [buckets]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateActiveDraft = (newId) => {
    if (!newId) {
      localStorageService.removeItem(COMMUNITY_MAGIC_REACH_KEY);
      setActiveDraft(null);
    } else {
      localStorageService.setItem(COMMUNITY_MAGIC_REACH_KEY, newId);
      fetchDraft(newId);
    }
  };

  const getAllBuckets = async () => {
    if (activeCommunity?._id) {
      setLoadingBuckets(true);
      const { data: response, error } = await getBucketDataByAdmin({
        communityId: activeCommunity?._id,
        eventId: currentRouterQuery?.eventId
      });

      setLoadingBuckets(false);
      if (error) {
        setErrorBucket(error);
        return;
      }
      if (response?.data) {
        setBuckets(
          response.data
            .filter((bucket) => bucket.totalUsers > 0)
            .sort((a, b) => {
              if (a.bucket === 'Members') {
                return -1;
              }
              if (b.bucket === 'Members') {
                return 1;
              }
              return a.bucket.localeCompare(b.bucket);
            })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const getAllDrafts = async () => {
    if (activeCommunity?._id) {
      setLoadingDraft(true);
      const { data, error } = await fetchAllDraftsByAdmin({
        communityId: activeCommunity?._id
      });
      setLoadingDraft(false);
      if (error) {
        setErrorDrafts(error);
        return;
      }
      if (data?.data) {
        const sortedData = data.data.sort(
          (a, b) =>
            new Date(b.lastModifiedTimeStamp) -
            new Date(a.lastModifiedTimeStamp)
        );
        setAllDrafts(sortedData);
        const draftIdInStorage = localStorageService.getItem(
          COMMUNITY_MAGIC_REACH_KEY
        );
        if (draftIdInStorage && !isEditingScheduledMessage) {
          updateActiveDraft(draftIdInStorage);
        }
        setDraftsInitialized(true);
      }
    }
  };

  const getAllSent = async () => {
    if (activeCommunity?._id) {
      setLoadingSent(true);
      const { data, error } = await fetchAllSentByAdmin({
        communityId: activeCommunity?._id
      });
      setLoadingSent(false);
      if (error) {
        setErrorSent(error);
      }
      const allMessages = data?.data;

      if (allMessages) {
        // Filter for messages with only the following status
        // sent, processing, processing_failed, reviewing, blocked, scheduled
        const sentMessages = allMessages.filter((data) => {
          return [
            MAGIC_REACH_MESSAGE_STATUS.SENT,
            MAGIC_REACH_MESSAGE_STATUS.PROCESSING,
            MAGIC_REACH_MESSAGE_STATUS.PROCESSING_FAILED,
            MAGIC_REACH_MESSAGE_STATUS.REVIEWING,
            MAGIC_REACH_MESSAGE_STATUS.BLOCKED,
            MAGIC_REACH_MESSAGE_STATUS.SCHEDULED
          ].includes(data.status);
        });

        // Get message date used to compare based on message status
        const getMessageDate = (message) => {
          if (message.status === MAGIC_REACH_MESSAGE_STATUS.SCHEDULED) {
            return new Date(message.schedule.dueAt);
          } else {
            return new Date(message.sentOn);
          }
        };

        // Sort messages by either sentOn date (or dueAt for scheduled messages)
        const sortedSentMessages = sentMessages.sort(
          (a, b) => getMessageDate(b) - getMessageDate(a)
        );

        setAllSent(sortedSentMessages);
        setDraftsInitialized(true);
      }
    }
  };

  const createDraft = async (
    {
      bucketFilters,
      bucketMetas,
      selectedBuckets = [],
      selectedUsers = [],
      unselectedUsers = [],
      isDraft = true,
      sentEmails = [],
      sentPlatforms = [],
      sentBucketName = '',
      title = '',
      content = null,
      author = user?._id,
      isDemo = false
    } = {},
    callback
  ) => {
    setIsEditingScheduledMessage(false);
    setHasSavedSchedule(false);

    const newDraftData = {
      title,
      content: content ?? rootNode,
      communityId: activeCommunity?._id,
      sentBucketName,
      sentEmails,
      author,
      isDraft,
      isDemo,
      sentPlatforms,
      selectedBuckets,
      selectedUsers,
      unselectedUsers
    };

    if (bucketFilters) {
      newDraftData.bucketFilters = bucketFilters;
    }

    if (bucketMetas) {
      newDraftData.bucketMetas = bucketMetas;
    }

    const { data, error } = await createMessageByAdmin(newDraftData);
    if (error) {
      console.error('error', error);
      return;
    }

    newDraftData._id = data?.data?.messageId;
    updateActiveDraft(data?.data?.messageId);
    setActiveDraft(newDraftData);

    callback?.(data?.data?.messageId);

    return data?.data?.messageId;
  };

  const updateDraft = async (updatedData) => {
    const latestActiveDraft = {
      ...activeDraft,
      ...updatedData
    };

    setActiveDraft(latestActiveDraft);
    // If is editing scheduled message, do not need to update every moment
    if (!isEditingScheduledMessage) {
      updateMessageByAdmin(latestActiveDraft, activeDraft?._id);
    }
  };

  const deleteMessage = async (id, isDraft) => {
    if (!id) {
      localStorageService.removeItem(COMMUNITY_MAGIC_REACH_KEY);
      return;
    }

    const { error } = await deleteMagicReachMessage(
      activeCommunity?._id,
      id
    );

    if (error) {
      showErrorToast(error);
      return;
    }

    if (isDraft) {
      getAllDrafts();
    } else {
      showToast({ text: t('message-deleted') });
      getAllSent();
    }
    localStorageService.removeItem(COMMUNITY_MAGIC_REACH_KEY);
    updateActiveDraft(null);
  };

  const initializeMagicReach = async () => {
    setDraftsInitialized(false);
    if (!allDrafts || allDrafts.length === 0) {
      await getAllDrafts();
    }
    if (!buckets || buckets.length === 0) {
      getAllBuckets();
    }
    if (!allSent || allSent.length === 0) {
      getAllSent();
    }
    if (currentRouterQuery.eventId) {
      localStorageService.removeItem(COMMUNITY_MAGIC_REACH_KEY);
    } else {
      let currentDraftId = localStorageService.getItem(
        COMMUNITY_MAGIC_REACH_KEY
      );
      updateActiveDraft(currentDraftId);
      setActiveDraft(
        allDrafts.find((draft) => draft._id === currentDraftId)
      );
    }
  };

  useEffect(() => {
    if (activeCommunity?._id) {
      initializeMagicReach();
    }
  }, [activeCommunity?._id]);

  const handleMagicReachSendErrors = () => {
    if (!activeDraft?.title) {
      showErrorToast(t('please-enter-a-subject'));
      return false;
    }
    if (!activeDraft?.content) {
      showErrorToast(t('please-add-some-content'));
      return false;
    }

    return true;
  };

  const handleRemoveSchedule = async () => {
    if (isEditingScheduledMessage) {
      const { error } = await removeScheduleFromMessage(
        activeCommunity?._id,
        activeDraft?._id
      );

      if (error) {
        showErrorToast(error);
        return;
      }
    }
    updateDraft({ status: MAGIC_REACH_MESSAGE_STATUS.DRAFT });
    setHasSavedSchedule(false);
    setIsEditingScheduledMessage(false);
    showToast({ text: t('schedule-removed') });

    return true;
  };

  const value = {
    draftsInitialized,
    allDrafts,
    allSent,
    activeDraft,
    loadingMessages: loadingDraft || loadingSent,
    loadingDraft,
    errorDrafts,
    errorSent,
    updateActiveDraft,
    createDraft,
    updateDraft,
    getAllDrafts,
    deleteMessage,
    // access
    visibilityType,
    setVisibilityType,
    // Bucket data
    buckets,
    bucketMap,
    loadingBuckets,
    errorBucket,
    getAllBuckets,
    user,
    updateMessageProgressively,
    initializeMagicReach,

    handleMagicReachSendErrors,

    setIsEditingScheduledMessage,
    isEditingScheduledMessage,

    setHasSavedSchedule,
    hasSavedSchedule,

    handleRemoveSchedule,
    getAllSent
  };
  return (
    <MagicReachContext.Provider value={value}>
      {children}
    </MagicReachContext.Provider>
  );
};

export const useMagicReachContext = () => useContext(MagicReachContext);
